import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BasePageComponent } from '../../base-page.component';
import {
  ApplicationSubType,
  Inspection,
  InspectionFilterType,
  InspectionPlugin,
  InspectionValidator,
  SearchFilter
} from '../../shared/models';
import {
  DataService,
  InspectionPluginService,
  InspectionService,
  LanguageService,
  MultiEditService,
  PluginModelService
} from '../../shared/services';
import { ChildrenInspectionListMenuComponent } from '../../menus/children-inspection-list.menu';
import { ConfirmComponent } from '../../modal/confirm.component';
import { Subscription, timer, zip } from 'rxjs';
import * as _ from 'lodash';
import { OrderSummariesComponent } from '../../utils';
import { InputLangComponent } from '../../modal/inputlang.component';
import { takeWhile, switchMap, tap } from 'rxjs/operators';

@Component({
  templateUrl: './children-list.component.html',
  standalone: false
})
export class ChildrenInspectionListPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  readonly defaultFilters: SearchFilter[] = [
    { type: InspectionFilterType.contentType, value: '100' },
    { type: InspectionFilterType.applicationSubType, value: `${ApplicationSubType.PreInstallation}` },
    { type: InspectionFilterType.applicationSubType, value: `${ApplicationSubType.Installation}` },
    { type: InspectionFilterType.applicationSubType, value: `${ApplicationSubType.Instruction}` }
  ];
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('orderPlugins', { static: true })
  orderPluginsModal: OrderSummariesComponent;
  @ViewChild('languagesModal', { static: true }) languageModal: InputLangComponent;
  loading: boolean = false;
  alive = true;
  validatedPlugins: Array<{ data: InspectionPlugin; messages: string[] }> = [];
  inspection: Inspection;
  filteredInspections: Inspection[];
  creatingInspections: Inspection[];
  inspectionId: string;
  plugins: any[];
  languages: any[];
  errorMessage: string;
  multiEditSubscription: Subscription;
  constructor(
    private inspectionService: InspectionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private pluginsService: InspectionPluginService,
    private language: LanguageService,
    private pluginModelService: PluginModelService,
    private multiEditService: MultiEditService,
    private dataService: DataService,
    location: Location
  ) {
    super(location);
  }

  ngOnInit(): void {
    this.initRoute();
  }

  openInspection(inspection: Inspection): void {
    this.router.navigate([this.inspectionsRoute, inspection.id]);
  }

  openMenu(group?: string): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(ChildrenInspectionListMenuComponent, {
      inspection: this.inspection,
      group
    });
  }

  selectMenu(item: any) {
    switch (item) {
      case 1:
        this.gotoInformationPage();
        break;
      case 2:
        this.gotoEditPage();
        break;
      case 3:
        this.gotoEditJsonPage();
        break;
      case 4:
        this.openDeleteConfirm();
        break;
      case 5:
        this.gotoCreateInspectionFromModelPage(ApplicationSubType.PreInstallation);
        break;
      case 6:
        this.gotoCreateInspectionFromModelPage(ApplicationSubType.Installation);
        break;
      case 7:
        this.gotoCreateInspectionFromModelPage(ApplicationSubType.Instruction);
        break;
      case 8:
        this.gotoAddPluginPage();
        break;
      case 9:
        this.openOrderPluginsPage();
        break;
      case 10:
        this.openLanguagesSelector();
        break;
      default:
    }
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  openOrderPluginsPage() {
    const orderableItems = _.map(this.inspection.plugins, (p: any) => ({
      id: p.id,
      name: this.language.getInstantValue(`plugins.${p.pluginType}.name`) || p.name || p.pluginType,
      type: 'plugin',
      order: p.order
    }));

    this.orderPluginsModal.open(orderableItems);
  }

  savePluginsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionService.orderPlugins(this.inspectionId, orderableItems).subscribe((el) => {});
  }

  deleteInspection(e: any) {
    this.inspectionService.deleteInspection(this.inspectionId, this.inspection.actionPlanId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'editjson']);
  }

  public gotoAddPluginPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'addPlugin', this.inspection.applicationSubType || '']);
  }

  gotoEditPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'edit']);
  }

  gotoCreateInspectionFromModelPage(applicationSubType: number) {
    return this.router.navigate([this.inspectionsRoute, 'selectMachine', this.inspectionId, applicationSubType]);
  }

  gotoInformationPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'view']);
  }

  gotoValidationSummaryPage() {
    if (this.validatedPlugins.length > 0) {
      return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'validations']);
    }
  }

  refreshPage() {
    this.refresh();
  }

  viewReport(inspection: Inspection) {
    this.inspectionService.viewReport(inspection.reportItemId);
  }

  public openLanguagesSelector(): void {
    const languageIndex = this.languages.findIndex((l) => l === this.inspection.inputLanguage);
    this.languageModal.modalFooter = true;
    this.languageModal.modalHeader = true;
    this.languageModal.modalMessage = true;
    this.languageModal.open({
      values: this.languages,
      selectedIndex: languageIndex
    });
  }

  public selectLanguagesMenu($event): void {
    if (this.inspection.inputLanguage !== $event) {
      this.inspectionService.changeInputLanguage(this.inspectionId, $event).subscribe((r) => {
        if (r) {
          location.reload();
        }
      });
    }
  }

  goToBack(): void {
    InspectionService.machineSerial
      ? this.router.navigate([this.appRoute, 'machines', InspectionService.machineSerial, 'inspections'])
      : InspectionService.clientNumber
      ? this.router.navigate([this.appRoute, 'customers', InspectionService.clientNumber, 'inspections'])
      : this.router.navigate([this.inspectionsRoute]);
  }

  exportInspections() {
    const search = {
      page: 0,
      pageSize: 1000000,
      filters: [...this.defaultFilters, { type: InspectionFilterType.parent, value: `${this.inspectionId}` }],
      minimumEtag: null
    };
    this.inspectionService.exportInspections(search);
  }

  get inspections() {
    if (!this.creatingInspections && !this.filteredInspections) return null;
    return [...(this.creatingInspections ?? []), ...(this.filteredInspections ?? [])];
  }

  private initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (!id) {
        this.router.navigate([this.inspectionsRoute]);
      }

      this.inspectionId = id;
      this.initData();
      this.multiEditSubscription = this.multiEditService.installationUpdate(this.inspectionId).subscribe(() => {
        this.creatingInspections = [];
        this.getInspections().subscribe();
        this.checkCreatingInspections();
      });
    });
  }

  private initData(): void {
    if (!this.isConnection) return;

    const isInspectionWasLoaded = this.dataService.getInspection(this.inspectionId)?.isLoaded;

    this.loading = true;
    zip(this.inspectionService.getInspection(this.inspectionId), this.getInspections()).subscribe(([inspection, inspections]) => {
      this.inspection = inspection;
      this.initPluginValidation(inspection);
      if (this.inspection.isNew) this.gotoEditPage();
      this.loading = false;
      if (!this.inspection.isOffline && !this.inspection.isSynchronizing)
        this.multiEditService.activate(this.inspectionId, this.inspection.etag, isInspectionWasLoaded);
    });

    this.language.getLanguages().subscribe((data) => {
      this.languages = data;
    });

    this.checkCreatingInspections();
  }

  private getInspections() {
    const search = {
      page: 0,
      pageSize: 1000,
      filters: [...this.defaultFilters, { type: InspectionFilterType.parent, value: `${this.inspectionId}` }],
      minimumEtag: null
    };

    return this.inspectionService
      .getFilteredInspections(search)
      .pipe(tap((inspections) => (this.filteredInspections = _.sortBy(inspections, 'applicationSubType'))));
  }

  private checkCreatingInspections() {
    this.alive = true;
    timer(0, 10000)
      .pipe(
        takeWhile(() => this.alive),
        switchMap(() =>
          this.inspectionService.getCreatingInspections({
            creatingInspections: this.creatingInspections?.map((i) => i.id) ?? [],
            search: {
              page: 0,
              pageSize: 1000,
              filters: [
                { type: InspectionFilterType.parent, value: `${this.inspectionId}` },
                { type: InspectionFilterType.personal, value: '100' },
                { type: InspectionFilterType.status, value: `-50` }
              ]
            }
          })
        )
      )
      .subscribe((inspections) => {
        inspections.filter((i) => i.status >= 0).forEach((i) => (i.isNew = true));
        this.creatingInspections = inspections;
        if (!this.creatingInspections?.filter((i) => i.status === -50).length) this.alive = false;
      });
  }

  private initPluginValidation(inspection: Inspection) {
    this.pluginModelService
      .getPluginModelsValidationSchemas(inspection.inspectionType.toString(), inspection.status.toString())
      .subscribe((validationSchemas) => {
        const validator = new InspectionValidator(validationSchemas);
        this.inspection.accept(validator);
        if (validator.hasErrors()) {
          this.errorMessage = 'error';
          this.validatedPlugins = validator.errors;
        }
      });
  }

  ngOnDestroy() {
    this.alive = false;
    this.multiEditSubscription?.unsubscribe();
  }
}
